import React, { useEffect, useState, useRef, lazy } from 'react';
interface LazyMountProps {
  children: React.ReactNode;
  /** Optional id placed on the placeholder wrapper so anchor links work
   *  before the section mounts. */
  id?: string;
  /** Pixels before the element enters the viewport to start mounting. Default 300. */
  rootMargin?: string;
  /** Min height of the placeholder so layout doesn't shift before mount. */
  minHeight?: number;
  /** If true, mount immediately (skip IntersectionObserver). */
  eager?: boolean;
}
/**
 * Mounts its children only when scrolled near the viewport.
 * Defers lazy chunks until the user actually needs them.
 * Accepts an `id` so `#anchor` links work even before mount.
 *
 * Listens for the global `mp:eager-mount` event so a CTA elsewhere on the
 * page can force every pending LazyMount to render immediately. This is
 * essential before programmatic smooth-scrolls so the target position is
 * computed against final layout (not placeholder min-heights).
 */
export function LazyMount({
  children,
  id,
  rootMargin = '300px 0px',
  minHeight = 400,
  eager = false
}: LazyMountProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shouldRender, setShouldRender] = useState(eager);
  useEffect(() => {
    if (shouldRender) return;
    // Global "mount everything now" hook for smooth-scroll CTAs.
    const onEagerMount = () => setShouldRender(true);
    window.addEventListener('mp:eager-mount', onEagerMount);
    return () => window.removeEventListener('mp:eager-mount', onEagerMount);
  }, [shouldRender]);
  useEffect(() => {
    if (eager || shouldRender) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldRender(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
          break;
        }
      }
    }, {
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [eager, rootMargin, shouldRender]);
  return <div ref={ref} id={id} style={shouldRender ? undefined : {
    minHeight
  }} data-id="element-272">
      {shouldRender ? children : null}
    </div>;
}